import { CheckedDataService, ICheckedEntity } from '@se/se.platform.base';
import { HardcodedData, JsonObject } from '@se/cp.common';
import { DataBinding } from '@se/se.platform.base';
import { AutomaticSetKineticComboParams, ISegmentCode } from './interfaces';

const ITEM_ID_MAP = {
    notValuated: 'ed90ca44-0a3e-4b64-b17c-c7995981dcfd',
    notEvaluable: '440df0c4-365d-4ded-b732-9c2ec13a209d',
    normal: '97ce11eb-f1a1-4077-85d5-af6c170f791b',
    hypokinesia: 'c33ae67e-a581-4ef5-be04-3275615d6c4c',
    hyperkinesia: '7e414309-ac52-492a-bde3-cae0a7cc5cde',
    regionalDyssynergies: '02015e07-6995-4312-bcbe-d6014bc2770a',
    septalDyssynergiaSecondaryToDelayedActivation: 'f9609e1d-1ae0-4b11-a48b-ac795326af77',
    septalDyssynergiaSecondaryToCEC: 'af4cb60c-0c87-48f0-8f19-45cd2d9d9ebe',
};

export class AutomaticSetKineticCombo {
    constructor(private dataSourceService: CheckedDataService) {}

    private parseExportSegment = (segment: ICheckedEntity, kineticEntries: JsonObject): ISegmentCode => {
        const regionID = segment.getPropertyPrimitiveValue('RegionID');
        const name = typeof regionID === 'string' ? regionID : '';
        return {
            name,
            code: this.parseSegmentCode(segment, kineticEntries),
        };
    };

    private parseSegmentCode = (segment: ICheckedEntity, kineticEntries: JsonObject): string => {
        const regionID = segment.getPropertyPrimitiveValue('RegionID');
        const name = typeof regionID === 'string' ? regionID : '';
        const entityValue = kineticEntries[name];
        if (!entityValue) return '';
        const _code = (entityValue as JsonObject)?.['Code'];
        const code = typeof _code === 'string' ? _code : '';
        return code;
    };

    private getSameValues = (exportSegments: ISegmentCode[]): string | null => {
        if (exportSegments.length === 0) return '0';
        const valueToCompare = exportSegments[0].code ?? '0';
        if (exportSegments.every((segment) => segment.code === valueToCompare)) {
            return valueToCompare;
        }
        return null;
    };

    private getComboValueToSet = (sameValues: string | null, currentValue: JsonObject, id: string) => {
        if (currentValue) return currentValue;
        const options = this.getOptions(id);
        switch (sameValues) {
            case '0':
                return options.find((item: JsonObject) => item.Id === ITEM_ID_MAP.notValuated) ?? null;
            case '1':
                return options.find((item: JsonObject) => item.Id === ITEM_ID_MAP.normal) ?? null;
            case '2':
                return options.find((item: JsonObject) => item.Id === ITEM_ID_MAP.hypokinesia) ?? null;
            case '4':
                return options.find((item: JsonObject) => item.Id === ITEM_ID_MAP.regionalDyssynergies) ?? null;
            case 'X':
                return options.find((item: JsonObject) => item.Id === ITEM_ID_MAP.notEvaluable) ?? null;
            default:
                return null;
        }
    };
    private getOptions = (id: string) => {
        /**@ts-ignore */
        const options = this.dataSourceService.getById?.(
            'lsUsClinicalEvaluationItem',
            [],
            [
                {
                    type: 'where',
                    propertyName: 'IdUsClinicalEvaluationCombo',
                    value: id,
                },
            ],
        );
        return options;
    };

    execute(params: AutomaticSetKineticComboParams) {
        const [kineticEntries, currentValue, id] = params;
        const segments = this.dataSourceService.getEntities(
            new DataBinding(HardcodedData.lists.leftVentricleSegmentList),
        );
        const segmentCodes: ISegmentCode[] =
            segments
                ?.map((segment) => this.parseExportSegment(segment, kineticEntries))
                .filter((segment) => segment.code !== null) ?? [];
        const sameValues = this.getSameValues(segmentCodes);
        return this.getComboValueToSet(sameValues, currentValue, id);
    }
}
